import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, MapPin, Clock, DollarSign, Truck, Sparkles, BadgeCheck, CalendarDays, Phone, Globe, Plus, CalendarX, HeartPulse, Briefcase, Stethoscope, Award } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Calendar } from "../components/ui/calendar";
import { COUNTRIES } from "../lib/countries";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const SHIFTS = ["Day", "Evening", "Night", "Flexible"];
const MODES = [
  { id: "onsite", label: "On-site" },
  { id: "remote", label: "Remote" },
  { id: "both", label: "Both" },
];
const SECTORS = [
  { id: "general", label: "General gig work", icon: Sparkles },
  { id: "healthcare", label: "Healthcare", icon: HeartPulse },
];

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const parseISO = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };

export default function FlexDashboard() {
  const { user } = useAuth();
  const [cats, setCats] = useState({ onsite: [], remote: [], health: [], employment_prefs: [] });
  const [form, setForm] = useState(null);
  const [skillsText, setSkillsText] = useState("");
  const [customCat, setCustomCat] = useState("");
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [c, p] = await Promise.all([api.get("/flex/categories"), api.get("/flex/profile")]);
        setCats({ onsite: c.data.onsite || [], remote: c.data.remote || [], health: c.data.health || [], employment_prefs: c.data.employment_prefs || [] });
        const d = p.data;
        setForm({
          name: d.name || user?.name || "",
          phone: d.phone || "",
          location: d.location || "",
          country: d.country || "",
          travel_radius: d.travel_radius || "",
          work_mode: d.work_mode || "onsite",
          categories: d.categories || [],
          days: d.days || [],
          shift: d.shift || "",
          blocked_dates: d.blocked_dates || [],
          start_date: d.start_date || "",
          hours_per_week: d.hours_per_week || "",
          desired_hourly_wage: d.desired_hourly_wage || "",
          has_transport: !!d.has_transport,
          summary: d.summary || "",
          verified: !!d.verified,
          credential_verified: !!d.credential_verified,
          sector: d.sector || "general",
          profession: d.profession || "",
          profession_other: d.profession_other || "",
          license_type: d.license_type || "",
          license_number: d.license_number || "",
          clinical_years: d.clinical_years ?? "",
          specialty: d.specialty || "",
          employment_pref: d.employment_pref || "",
        });
        setSkillsText((d.skills || []).join(", "));
      } catch (e) {
        toast.error(apiErr(e));
      }
    })();
  }, [user]);

  const toggle = (key, val) =>
    setForm((f) => ({ ...f, [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val] }));
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addCustomCat = () => {
    const c = customCat.trim();
    if (!c) return;
    if (!form.categories.includes(c)) set("categories", [...form.categories, c]);
    setCustomCat("");
  };

  const getVerified = async () => {
    setVerifying(true);
    try {
      const r = await api.post("/flex/verify/checkout", { kind: "flex_verify", origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(apiErr(e));
      setVerifying(false);
    }
  };

  const getCredentialVerified = async () => {
    setVerifying(true);
    try {
      const r = await api.post("/health/verify/checkout", { kind: "health_verify", origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(apiErr(e));
      setVerifying(false);
    }
  };

  const save = async () => {
    if (!form.phone.trim()) {
      toast.error("Phone number is required so employers can reach you.");
      return;
    }
    if (form.sector === "healthcare") {
      if (!form.profession) { toast.error("Select your healthcare profession."); return; }
      if (form.profession === "Other" && !form.profession_other.trim()) { toast.error("Type your profession for 'Other'."); return; }
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        travel_radius: form.travel_radius ? Number(form.travel_radius) : null,
        hours_per_week: form.hours_per_week ? Number(form.hours_per_week) : null,
        desired_hourly_wage: form.desired_hourly_wage ? Number(form.desired_hourly_wage) : null,
        clinical_years: form.clinical_years !== "" ? Number(form.clinical_years) : null,
        skills: skillsText.split(",").map((s) => s.trim()).filter(Boolean),
      };
      await api.put("/flex/profile", payload);
      toast.success(form.sector === "healthcare"
        ? "Healthcare profile saved — you're visible in Darriker Health."
        : "Flex profile saved — you're visible to employers.");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <div className="p-10 font-mono2 text-sm">Loading…</div>;

  const isHealth = form.sector === "healthcare";
  const baseCats = form.work_mode === "remote" ? cats.remote : form.work_mode === "both" ? [...cats.onsite, ...cats.remote] : cats.onsite;
  const allCats = Array.from(new Set([...baseCats, ...form.categories]));
  const isOnsite = form.work_mode !== "remote";
  const done = isHealth
    ? [form.phone, form.profession, form.days.length, form.desired_hourly_wage, form.location].filter(Boolean).length
    : [form.phone, form.categories.length, form.days.length, form.desired_hourly_wage, form.location].filter(Boolean).length;

  return (
    <div className="max-w-4xl mx-auto px-5 py-8" data-testid="flex-dashboard">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display font-black text-3xl tracking-tighter">{isHealth ? "Healthcare Worker Profile" : "Flex Worker Profile"}</h1>
          <p className="text-sm text-[#4B5563]">{isHealth ? "Add your profession, credentials & availability — facilities search and reach out to you." : "Set your availability & wage — employers search and reach out to you."}</p>
        </div>
        <div className="font-mono2 text-xs uppercase text-[#4B5563] border border-[#E5E7EB] px-3 py-1.5" data-testid="flex-completeness">
          {done}/5 essentials
        </div>
      </div>

      <div className="border border-[#0A0A0A] p-6 space-y-6">
        {/* sector */}
        <div>
          <Label icon={HeartPulse}>What kind of work do you offer?</Label>
          <div className="grid grid-cols-2 gap-2 mt-2 max-w-md" data-testid="flex-sector">
            {SECTORS.map((s) => {
              const SIcon = s.icon;
              return (
                <button
                  key={s.id}
                  type="button"
                  data-testid={`flex-sector-${s.id}`}
                  onClick={() => set("sector", s.id)}
                  className={`py-2.5 text-sm font-medium border inline-flex items-center justify-center gap-2 ${form.sector === s.id ? (s.id === "healthcare" ? "bg-[#0E7C66] text-white border-[#0E7C66]" : "bg-[#002FA7] text-white border-[#002FA7]") : "border-[#0A0A0A] hover:bg-[#F3F4F6]"}`}
                >
                  <SIcon size={15} /> {s.label}
                </button>
              );
            })}
          </div>
        </div>

        {!isHealth && (
        <div>
          <Label icon={Globe}>How do you want to work?</Label>
          <div className="grid grid-cols-3 gap-2 mt-2 max-w-md" data-testid="flex-workmode">
            {MODES.map((m) => (
              <button
                key={m.id}
                type="button"
                data-testid={`flex-mode-${m.id}`}
                onClick={() => set("work_mode", m.id)}
                className={`py-2.5 text-sm font-medium border ${form.work_mode === m.id ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-[#0A0A0A] hover:bg-[#F3F4F6]"}`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>
        )}

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Full name" testid="flex-name" value={form.name} onChange={(v) => set("name", v)} />
          <Field label="Phone number — required" icon={Phone} testid="flex-phone" value={form.phone} onChange={(v) => set("phone", v)} />
          <Field label="City" icon={MapPin} testid="flex-location" value={form.location} onChange={(v) => set("location", v)} />
          <label className="block">
            <Label icon={Globe}>Country</Label>
            <select
              data-testid="flex-country"
              value={form.country}
              onChange={(e) => set("country", e.target.value)}
              className="mt-2 w-full border border-[#0A0A0A] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
            >
              <option value="">Select country</option>
              {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          {isOnsite && <Field label="Willing to travel (km)" type="number" testid="flex-travel" value={form.travel_radius} onChange={(v) => set("travel_radius", v)} />}
        </div>

        {/* categories (general) / profession + credentials (healthcare) */}
        {isHealth ? (
          <div className="space-y-4" data-testid="health-fields">
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="block">
                <Label icon={Stethoscope}>Profession</Label>
                <select
                  data-testid="health-profession"
                  value={form.profession}
                  onChange={(e) => set("profession", e.target.value)}
                  className="mt-2 w-full border border-[#0A0A0A] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0E7C66]"
                >
                  <option value="">Select your profession</option>
                  {cats.health.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              {form.profession === "Other" && (
                <Field label="Your profession" testid="health-profession-other" value={form.profession_other} onChange={(v) => set("profession_other", v)} />
              )}
              <label className="block">
                <Label icon={Briefcase}>Employment preference</Label>
                <select
                  data-testid="health-employment-pref"
                  value={form.employment_pref}
                  onChange={(e) => set("employment_pref", e.target.value)}
                  className="mt-2 w-full border border-[#0A0A0A] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0E7C66]"
                >
                  <option value="">Any</option>
                  {cats.employment_prefs.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </label>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="License / certification type" icon={Award} testid="health-license-type" value={form.license_type} onChange={(v) => set("license_type", v)} />
              <Field label="License number" testid="health-license-number" value={form.license_number} onChange={(v) => set("license_number", v)} />
              <Field label="Years of clinical experience" type="number" testid="health-clinical-years" value={form.clinical_years} onChange={(v) => set("clinical_years", v)} />
              <Field label="Specialty (e.g. ICU, ER, Pediatrics)" testid="health-specialty" value={form.specialty} onChange={(v) => set("specialty", v)} />
            </div>
          </div>
        ) : (
          <div>
            <Label icon={Sparkles}>What work are you interested in?</Label>
            <div className="flex flex-wrap gap-2 mt-2" data-testid="flex-categories">
              {allCats.map((c) => (
                <button
                  key={c}
                  type="button"
                  data-testid={`flex-cat-${c}`}
                  onClick={() => toggle("categories", c)}
                  className={`px-3 py-1.5 text-xs font-medium border ${form.categories.includes(c) ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-[#0A0A0A] hover:bg-[#F3F4F6]"}`}
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-3 max-w-sm">
              <input
                data-testid="flex-custom-cat"
                value={customCat}
                onChange={(e) => setCustomCat(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustomCat())}
                placeholder="Other — add your own"
                className="border border-[#0A0A0A] px-3 py-1.5 text-sm flex-1 focus:outline-none focus:ring-1 focus:ring-[#002FA7]"
              />
              <button type="button" data-testid="flex-add-cat" onClick={addCustomCat} className="btn-outline px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1"><Plus size={14} /> Add</button>
            </div>
          </div>
        )}

        {/* recurring weekly availability */}
        <div>
          <Label icon={CalendarDays}>Recurring days available</Label>
          <div className="flex flex-wrap gap-2 mt-2" data-testid="flex-days">
            {DAYS.map((d) => (
              <button
                key={d}
                type="button"
                data-testid={`flex-day-${d}`}
                onClick={() => toggle("days", d)}
                className={`px-3 py-1.5 text-xs font-medium border ${form.days.includes(d) ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#0A0A0A] hover:bg-[#F3F4F6]"}`}
              >
                {d}
              </button>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          <label className="block">
            <Label icon={Clock}>Preferred shift</Label>
            <select
              data-testid="flex-shift"
              value={form.shift}
              onChange={(e) => set("shift", e.target.value)}
              className="mt-2 w-full border border-[#0A0A0A] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
            >
              <option value="">Any</option>
              {SHIFTS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <Field label="Hours / week" type="number" testid="flex-hours" value={form.hours_per_week} onChange={(v) => set("hours_per_week", v)} />
          <Field label="Available from" type="date" testid="flex-start" value={form.start_date} onChange={(v) => set("start_date", v)} />
        </div>

        {/* block off specific dates */}
        <div>
          <Label icon={CalendarX}>Block off dates you're NOT available</Label>
          <p className="font-mono2 text-xs text-[#9CA3AF] mt-1">Click dates to mark unavailable. Employers see your open availability clearly.</p>
          <div className="mt-2 border border-[#0A0A0A] inline-block" data-testid="flex-blocked-calendar">
            <Calendar
              mode="multiple"
              selected={form.blocked_dates.map(parseISO)}
              onSelect={(dates) => set("blocked_dates", (dates || []).map(iso))}
            />
          </div>
          {form.blocked_dates.length > 0 && (
            <p className="text-xs text-[#D97706] mt-2" data-testid="flex-blocked-count">{form.blocked_dates.length} date{form.blocked_dates.length === 1 ? "" : "s"} blocked off</p>
          )}
        </div>

        <div className="grid sm:grid-cols-2 gap-4 items-end">
          <Field label="Desired hourly wage ($)" icon={DollarSign} type="number" testid="flex-wage" value={form.desired_hourly_wage} onChange={(v) => set("desired_hourly_wage", v)} />
          {isOnsite && !isHealth && (
            <label className="flex items-center gap-2.5 text-sm py-2.5 cursor-pointer" data-testid="flex-transport">
              <input type="checkbox" checked={form.has_transport} onChange={(e) => set("has_transport", e.target.checked)} className="w-4 h-4 accent-[#002FA7]" />
              <Truck size={16} className="text-[#4B5563]" /> I have my own transport
            </label>
          )}
        </div>

        <div>
          <Label>Skills (comma separated)</Label>
          <input
            data-testid="flex-skills"
            value={skillsText}
            onChange={(e) => setSkillsText(e.target.value)}
            placeholder="Forklift, Packing, Data entry, Customer service"
            className="mt-2 w-full border border-[#0A0A0A] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
          />
        </div>

        <div>
          <Label>Short bio</Label>
          <textarea
            data-testid="flex-summary"
            value={form.summary}
            onChange={(e) => set("summary", e.target.value)}
            rows={3}
            placeholder="Reliable warehouse associate, 3 years experience, available weekends and evenings."
            className="mt-2 w-full border border-[#0A0A0A] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
          />
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-[#E5E7EB] flex-wrap gap-3">
          {isHealth ? (
            form.credential_verified ? (
              <span className="font-mono2 text-xs text-[#0E7C66] inline-flex items-center gap-1.5 font-medium" data-testid="health-credential-badge">
                <Award size={16} /> Credential Verified
              </span>
            ) : (
              <button
                type="button"
                data-testid="health-get-verified-btn"
                onClick={getCredentialVerified}
                disabled={verifying}
                className="px-4 py-2 text-xs font-medium inline-flex items-center gap-1.5 border border-[#0E7C66] text-[#0E7C66] hover:bg-[#0E7C66] hover:text-white transition-colors disabled:opacity-50"
              >
                {verifying ? <Loader2 size={14} className="animate-spin" /> : <Award size={14} />} Get Credential Verified — $20 (boosts your ranking)
              </button>
            )
          ) : form.verified ? (
            <span className="font-mono2 text-xs text-[#10B981] inline-flex items-center gap-1.5 font-medium" data-testid="flex-verified-badge">
              <BadgeCheck size={16} /> Verified worker
            </span>
          ) : (
            <button
              type="button"
              data-testid="flex-get-verified-btn"
              onClick={getVerified}
              disabled={verifying}
              className="btn-outline px-4 py-2 text-xs font-medium inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              {verifying ? <Loader2 size={14} className="animate-spin" /> : <BadgeCheck size={14} />} Get Verified — $20 (boosts your ranking)
            </button>
          )}
          <button
            data-testid="flex-save-btn"
            onClick={save}
            disabled={saving}
            className="btn-ink px-6 py-2.5 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save profile
          </button>
        </div>
      </div>
    </div>
  );
}

function Label({ icon: Icon, children }) {
  return (
    <span className="font-mono2 text-xs uppercase text-[#4B5563] inline-flex items-center gap-1.5">
      {Icon && <Icon size={13} />} {children}
    </span>
  );
}

function Field({ label, value, onChange, type = "text", testid, icon: Icon }) {
  return (
    <label className="block">
      <Label icon={Icon}>{label}</Label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full border border-[#0A0A0A] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
      />
    </label>
  );
}
